import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API } from '../App';
import Layout from '../components/Layout';
import QRScannerDialog from '../components/QRScannerDialog';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Camera, Building2, Wrench, ClipboardList, AlertCircle, Eye } from 'lucide-react';
import { toast } from 'sonner';

export default function Scan() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);   // open scanner immediately on page load
  const [result, setResult] = useState(null);  // { type, entity, building }
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const handleScanned = async (code) => {
    setError(null);
    setResult(null);
    setBusy(true);
    try {
      const r = await axios.get(`${API}/scan/lookup?code=${encodeURIComponent(code)}`, { withCredentials: true });
      setResult(r.data);
      toast.success(`Found ${r.data.type}: ${r.data.entity.name || code}`);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Not found';
      setError(`${msg} (scanned: ${code})`);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const openWO = () => {
    if (!result) return;
    const e = result.entity;
    const buildingId = result.type === 'asset' ? e.building_id : e.id;
    // Navigate to /work-orders and let user create WO (asset_id pre-filled via query)
    navigate(`/work-orders?building=${buildingId}${result.type === 'asset' ? `&asset=${e.id}` : ''}`);
  };

  return (
    <Layout>
      <div data-testid="scan-page" className="space-y-6 max-w-2xl mx-auto">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2 text-slate-900 dark:text-slate-100"><Camera className="w-7 h-7 text-emerald-500" />Scan QR</h1>
          <p className="text-sm text-muted-foreground dark:text-slate-400">Point the camera at an asset or building sticker — we'll resolve it and let you dispatch a work order on the spot.</p>
        </div>

        {!open && !result && !error && (
          <Card className="p-8 text-center">
            <Camera className="w-14 h-14 mx-auto text-slate-300 mb-3" />
            <p className="text-sm text-muted-foreground mb-4">Camera not active.</p>
            <Button data-testid="reopen-scanner" onClick={() => setOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white">Open Camera</Button>
          </Card>
        )}

        {error && (
          <Card className="p-6 border-rose-200 bg-rose-50 dark:bg-rose-900/20 dark:border-rose-800/50">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold text-rose-900 dark:text-rose-200">No match</div>
                <div className="text-sm text-rose-700 dark:text-rose-300 break-all">{error}</div>
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <Button data-testid="scan-again-error" size="sm" onClick={() => { setError(null); setOpen(true); }}>Scan again</Button>
            </div>
          </Card>
        )}

        {result && (
          <Card data-testid="scan-result-card" className="p-6 border-emerald-300 bg-emerald-50/60 dark:bg-emerald-900/20 dark:border-emerald-700/50">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/15">
                {result.type === 'asset' ? <Wrench className="w-5 h-5 text-emerald-700 dark:text-emerald-200" /> : <Building2 className="w-5 h-5 text-emerald-700 dark:text-emerald-200" />}
              </div>
              <div className="flex-1">
                <div className="text-xs uppercase tracking-wide text-emerald-600 dark:text-emerald-300 font-semibold">{result.type}</div>
                <div className="text-lg font-bold text-foreground dark:text-slate-100">{result.entity.name}</div>
                {result.type === 'asset' && (
                  <div className="text-xs text-muted-foreground mt-1">
                    {result.entity.category} · {result.building?.name || '—'} · status <span className="capitalize font-medium">{result.entity.status}</span>
                  </div>
                )}
                {result.type === 'building' && (
                  <div className="text-xs text-muted-foreground mt-1">
                    {result.entity.location || result.entity.address || '—'}
                  </div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-5">
              <Button
                data-testid="scan-open-detail"
                variant="outline"
                onClick={() => navigate(result.type === 'asset' ? `/assets` : `/buildings`)}
              ><Eye className="w-4 h-4 mr-1" />Open {result.type}</Button>
              <Button
                data-testid="scan-create-wo"
                className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
                onClick={openWO}
                disabled={busy}
              ><ClipboardList className="w-4 h-4 mr-1" />Create Work Order</Button>
              <Button data-testid="scan-again" variant="outline" onClick={() => { setResult(null); setOpen(true); }}>Scan another</Button>
            </div>
          </Card>
        )}

        <QRScannerDialog open={open} onOpenChange={setOpen} onScan={handleScanned} />
      </div>
    </Layout>
  );
}
